<template>
  <app-content class="h-panel">
    <div class="h-panel-bar border-bottom">
      <span class="h-panel-title">序时账</span>
      <div class="h-panel-right">
        <Checkbox v-model="showNumPrice" class="mr-10">显示数量金额</Checkbox>
        <Button @click="showFilterModal = true" icon="h-icon-filter">筛选</Button>
        <Button color="primary" @click="exportData" icon="h-icon-outbox">导出</Button>
      </div>
    </div>
    
    <div class="chronological-body">
      <!-- 批量操作栏 -->
      <div class="batch-bar" v-if="selectedVoucherIds.length > 0">
        <span class="selected-count">已选 {{ selectedVoucherIds.length }} 张凭证</span>
        <div class="batch-actions">
          <Button v-if="currentAccountSets.voucherReviewed" color="blue" size="s" @click="batchAudit" icon="h-icon-check">批量审核</Button>
          <Button v-if="currentAccountSets.voucherReviewed" color="yellow" size="s" @click="batchCancelAudit" icon="h-icon-close">批量反审核</Button>
          <Button color="red" size="s" @click="batchDelete" icon="h-icon-trash">批量删除</Button>
          <Button size="s" @click="clearSelection">取消选择</Button>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="table-wrapper">
        <Loading :loading="loading" text="数据加载中..."></Loading>
        <table class="premium-table">
          <thead>
            <tr>
              <th width="40"><Checkbox :indeterminate="isIndeterminate" v-model="allSelected" @change="toggleAllVouchers"></Checkbox></th>
              <th width="100">日期</th>
              <th width="100">凭证字号</th>
              <th width="120">科目编码</th>
              <th width="200">科目名称</th>
              <th>摘要</th>
              <th width="120" class="text-right">借方金额</th>
              <th width="120" class="text-right">贷方金额</th>
              <th v-if="showNumPrice" width="100" class="text-right">数量</th>
              <th v-if="showNumPrice" width="100" class="text-right">单价</th>
              <th v-if="currentAccountSets.voucherReviewed" width="80">状态</th>
              <th width="100">操作人</th>
            </tr>
          </thead>
          <tbody v-if="groupedData.length > 0">
            <template v-for="group in groupedData">
              <!-- 凭证组首行 -->
              <tr :key="'group_' + group.voucherId" class="voucher-row-top" :class="{ 'audited': group.isAudited }">
                <td :rowspan="group.records.length">
                  <Checkbox v-model="selectedVoucherIds" :value="group.voucherId"></Checkbox>
                </td>
                <td :rowspan="group.records.length" class="cell-date">{{ group.voucherDate }}</td>
                <td :rowspan="group.records.length" class="cell-voucher">
                  <router-link :to="{name:'VoucherForm', params:{voucherId:group.voucherId}}" class="voucher-link">
                    {{ group.word }}-{{ group.code }}
                  </router-link>
                  <div class="voucher-tags">
                    <span v-if="group.isCarryForward" class="tag tag-blue">结转</span>
                  </div>
                </td>
                
                <!-- 第一条分录 -->
                <td class="cell-code">{{ group.records[0].subjectCode }}</td>
                <td class="cell-subject">{{ group.records[0].subjectName }}</td>
                <td class="cell-summary">{{ group.records[0].summary }}</td>
                <td class="text-right text-借 cell-amount">{{ group.records[0].debitAmount | numFormat }}</td>
                <td class="text-right text-贷 cell-amount">{{ group.records[0].creditAmount | numFormat }}</td>
                <td v-if="showNumPrice" class="text-right cell-qty">{{ group.records[0].num | numFormat }}</td>
                <td v-if="showNumPrice" class="text-right cell-price">{{ group.records[0].price | numFormat }}</td>
                
                <td v-if="currentAccountSets.voucherReviewed" :rowspan="group.records.length" class="cell-status">
                  <span v-if="group.isAudited" class="badge badge-success" :title="'审核人:' + group.auditor">已审核</span>
                  <span v-else class="badge badge-warning">未审核</span>
                </td>
                <td :rowspan="group.records.length" class="cell-member">
                  <div class="member-info">
                    <span class="name">{{ group.creator }}</span>
                    <span class="time">{{ group.createDate | formatDate('MM-dd') }}</span>
                  </div>
                </td>
              </tr>
              <!-- 凭证组剩余行 -->
              <tr v-for="(record, index) in group.records.slice(1)" :key="'record_' + record.detailsId" class="voucher-row-inner" :class="{ 'audited': group.isAudited }">
                <td class="cell-code" :class="{'last-row': index === group.records.length - 2}">{{ record.subjectCode }}</td>
                <td class="cell-subject" :class="{'last-row': index === group.records.length - 2}">{{ record.subjectName }}</td>
                <td class="cell-summary" :class="{'last-row': index === group.records.length - 2}">{{ record.summary }}</td>
                <td class="text-right text-借 cell-amount" :class="{'last-row': index === group.records.length - 2}">{{ record.debitAmount | numFormat }}</td>
                <td class="text-right text-贷 cell-amount" :class="{'last-row': index === group.records.length - 2}">{{ record.creditAmount | numFormat }}</td>
                <td v-if="showNumPrice" class="text-right cell-qty" :class="{'last-row': index === group.records.length - 2}">{{ record.num | numFormat }}</td>
                <td v-if="showNumPrice" class="text-right cell-price" :class="{'last-row': index === group.records.length - 2}">{{ record.price | numFormat }}</td>
              </tr>
              <!-- 凭证组间距行 -->
              <tr :key="'spacer_' + group.voucherId" class="spacer-row">
                <td colspan="100"></td>
              </tr>
            </template>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="100" class="text-center no-data">
                <div class="no-data-content">
                  <i class="h-icon-info"></i>
                  <p>未找到符合条件的凭证数据</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页栏 -->
      <div class="h-panel-bar border-top mt-10" v-if="pagination.total > 0">
        <Pagination
          :total="pagination.total"
          :current="pagination.current"
          :size="pagination.size"
          @change="handlePageChange"
          show-total
          show-sizer
          :size-options="[20, 50, 100, 200]"
          @sizechange="handlePageSizeChange"
        />
      </div>
    </div>

    <!-- 筛选弹窗 -->
    <Modal v-model="showFilterModal" title="高级筛选" :width="650">
      <Form :label-width="100" class="filter-form">
        <Row :space="20">
          <Cell :span="12">
            <FormItem label="开始日期">
              <DatePicker v-model="filterForm.startDate" type="date" placeholder="选择日期" block></DatePicker>
            </FormItem>
          </Cell>
          <Cell :span="12">
            <FormItem label="结束日期">
              <DatePicker v-model="filterForm.endDate" type="date" placeholder="选择日期" block></DatePicker>
            </FormItem>
          </Cell>
        </Row>
        <Row :space="20">
          <Cell :span="12">
            <FormItem label="最小金额">
              <NumberInput v-model="filterForm.minAmount" placeholder="0.00"></NumberInput>
            </FormItem>
          </Cell>
          <Cell :span="12">
            <FormItem label="最大金额">
              <NumberInput v-model="filterForm.maxAmount" placeholder="0.00"></NumberInput>
            </FormItem>
          </Cell>
        </Row>
        <Row :space="20">
          <Cell :span="12">
            <FormItem label="科目编码">
              <input v-model="filterForm.subjectCode" placeholder="支持模糊搜索" class="h-input"/>
            </FormItem>
          </Cell>
          <Cell :span="12">
            <FormItem label="制单人">
              <input v-model="filterForm.createMember" placeholder="姓名或昵称" class="h-input"/>
            </FormItem>
          </Cell>
        </Row>
        <Row :space="20">
          <Cell :span="12">
            <FormItem label="凭证字号">
              <input v-model="filterForm.voucherWord" placeholder="记/转/等" class="h-input"/>
            </FormItem>
          </Cell>
          <Cell :span="12">
            <FormItem label="摘要关键字">
              <input v-model="filterForm.summaryKeyword" placeholder="摘要内容搜索" class="h-input"/>
            </FormItem>
          </Cell>
        </Row>
      </Form>
      <div slot="footer">
        <Button @click="clearFilter">清空</Button>
        <Button color="primary" @click="applyFilter">应用筛选</Button>
        <Button @click="showFilterModal = false">取消</Button>
      </div>
    </Modal>
  </app-content>
</template>

<script>
import AccountBook from '../../api/accountbook';
import Voucher from '../../api/voucher';

export default {
  name: "ChronologicalLedger",
  data() {
    return {
      loading: false,
      showNumPrice: false,
      showFilterModal: false,
      datalist: [],
      selectedVoucherIds: [],
      filterForm: {
        startDate: null,
        endDate: null,
        minAmount: null,
        maxAmount: null,
        createMember: '',
        subjectCode: '',
        voucherWord: '',
        summaryKeyword: ''
      },
      hasFilter: false,
      pagination: {
        current: 1,
        size: 50,
        total: 0
      }
    }
  },
  computed: {
    // 将扁平的数据按凭证 ID 进行分组
    groupedData() {
      if (!this.datalist || this.datalist.length === 0) return [];
      
      const groups = [];
      let currentGroup = null;
      
      this.datalist.forEach(item => {
        if (!currentGroup || currentGroup.voucherId !== item.voucherId) {
          currentGroup = {
            voucherId: item.voucherId,
            voucherDate: item.voucherDate,
            word: item.word,
            code: item.code,
            isAudited: !!item.auditMemberName,
            auditor: item.auditMemberName,
            isCarryForward: !!item.carryForward,
            creator: item.createMemberRealName || item.createMember || '系统',
            createDate: item.voucherDate, // 这里后端 VO 可能没带 createDate，暂用凭证日期
            records: []
          };
          groups.push(currentGroup);
        }
        currentGroup.records.push(item);
      });
      
      return groups;
    },
    allSelected: {
      get() {
        const voucherIds = this.groupedData.map(g => g.voucherId);
        return voucherIds.length > 0 && voucherIds.every(id => this.selectedVoucherIds.includes(id));
      },
      set(val) {
        if (val) {
          this.selectedVoucherIds = this.groupedData.map(g => g.voucherId);
        } else {
          this.selectedVoucherIds = [];
        }
      }
    },
    isIndeterminate() {
      const selectedCount = this.selectedVoucherIds.length;
      return selectedCount > 0 && selectedCount < this.groupedData.length;
    },
    currentAccountSets() {
      return this.$store.state.currentAccountSets || {};
    }
  },
  watch: {
    showNumPrice() {
      this.loadData();
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      let apiCall;
      
      if (this.hasFilter) {
        // 筛选模式不支持后端分页（现有 API 限制），故请求全量后前端展示
        apiCall = AccountBook.loadChronologicalLedgerWithFilter(this.filterForm);
      } else {
        apiCall = AccountBook.loadChronologicalLedger({
          showNumPrice: this.showNumPrice,
          page: this.pagination.current,
          size: this.pagination.size
        });
      }

      apiCall.then(res => {
        if (this.hasFilter) {
          this.datalist = res.data || [];
          this.pagination.total = this.datalist.length;
        } else {
          const pageData = res.data || {};
          this.datalist = pageData.records || [];
          this.pagination.total = pageData.total || 0;
          this.pagination.current = pageData.current || 1;
        }
        this.selectedVoucherIds = []; // 翻页或重新加载时清空选择
      }).catch(err => {
        this.$Message.error('加载序时账数据失败');
        console.error(err);
      }).finally(() => {
        this.loading = false;
      });
    },
    applyFilter() {
      this.hasFilter = true;
      this.pagination.current = 1;
      this.showFilterModal = false;
      this.loadData();
    },
    clearFilter() {
      this.filterForm = {
        startDate: null,
        endDate: null,
        minAmount: null,
        maxAmount: null,
        createMember: '',
        subjectCode: '',
        voucherWord: '',
        summaryKeyword: ''
      };
      this.hasFilter = false;
      this.loadData();
    },
    handlePageChange(page) {
      if (typeof page === 'object' && page !== null) {
        const nextPage = page.cur || page.page || page.current;
        const nextSize = page.size || page.pageSize;
        if (nextSize !== undefined && nextSize !== null) {
          this.pagination.size = Number(nextSize) || this.pagination.size;
        }
        this.pagination.current = Number(nextPage) || 1;
      } else {
        this.pagination.current = Number(page) || 1;
      }
      this.loadData();
    },
    handlePageSizeChange(size) {
      if (typeof size === 'object' && size !== null) {
        const nextSize = size.size || size.pageSize || size.value;
        this.pagination.size = Number(nextSize) || this.pagination.size;
      } else {
        this.pagination.size = Number(size) || this.pagination.size;
      }
      this.pagination.current = 1;
      this.loadData();
    },
    exportData() {
      let params = {};
      if (this.hasFilter) {
        params = {...this.filterForm};
      }
      const queryString = Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== '')
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join('&');
      window.open(`/api/accountbook/export/chronologicalLedger?${queryString}`);
    },
    // 批量操作
    batchAudit() {
      if (this.selectedVoucherIds.length === 0) return;
      const period = this.resolveSelectedPeriod();
      if (!period) {
        this.$Message.warning('请先选择同一会计期间的凭证');
        return;
      }
      this.$Confirm('确认批量审核选中的凭证吗？', '批量审核').then(() => {
        this.submitBatchAudit(period, false);
      });
    },
    submitBatchAudit(period, confirmRisk) {
      const params = {
        checked: this.selectedVoucherIds.slice(),
        year: period.year,
        month: period.month
      };
      if (confirmRisk) {
        params.confirmRisk = true;
      }
      Voucher.audit(params).then(() => {
        this.$Message.success('批量审核成功');
        this.loadData();
      }).catch((error) => {
        if (!confirmRisk && this.needAuditRiskConfirm(error)) {
          return this.$Confirm('检测到同人制单+审核风险，是否继续并记录风险标记？', '风险确认')
            .then(() => this.submitBatchAudit(period, true))
            .catch(() => {});
        }
      });
    },
    batchCancelAudit() {
      if (this.selectedVoucherIds.length === 0) return;
      const period = this.resolveSelectedPeriod();
      if (!period) {
        this.$Message.warning('请先选择同一会计期间的凭证');
        return;
      }
      this.$Confirm('确认批量反审核选中的凭证吗？', '批量反审核').then(() => {
        Voucher.cancelAudit({
          checked: this.selectedVoucherIds,
          year: period.year,
          month: period.month
        }).then(() => {
          this.$Message.success('批量反审核成功');
          this.loadData();
        });
      });
    },
    batchDelete() {
      if (this.selectedVoucherIds.length === 0) return;
      const period = this.resolveSelectedPeriod();
      if (!period) {
        this.$Message.warning('请先选择同一会计期间的凭证');
        return;
      }
      this.$Confirm('确定要删除选中的凭证吗？此操作不可撤销！', '高危操作').then(() => {
        Voucher.batchDelete({
          checked: this.selectedVoucherIds,
          year: period.year,
          month: period.month
        }).then(() => {
          this.$Message.success('批量删除成功');
          this.loadData();
        });
      });
    },
    resolveSelectedPeriod() {
      if (this.selectedVoucherIds.length === 0) {
        return null;
      }
      const periodMap = {};
      this.groupedData.forEach(group => {
        if (this.selectedVoucherIds.includes(group.voucherId)) {
          const dateText = String(group.voucherDate || '');
          const parts = dateText.split('-');
          if (parts.length >= 2) {
            const year = Number(parts[0]);
            const month = Number(parts[1]);
            if (!Number.isNaN(year) && !Number.isNaN(month)) {
              periodMap[`${year}-${month}`] = { year, month };
            }
          }
        }
      });
      const periods = Object.keys(periodMap);
      if (periods.length !== 1) {
        return null;
      }
      return periodMap[periods[0]];
    },
    needAuditRiskConfirm(error) {
      const msg = error && error.msg ? error.msg : '';
      return msg.indexOf('同人制单+审核风险') > -1 || msg.indexOf('confirmRisk=true') > -1;
    },
    clearSelection() {
      this.selectedVoucherIds = [];
    },
    toggleAllVouchers() {
      // computed state setter handles this
    }
  }
};
</script>

<style lang="less" scoped>
.chronological-body {
  padding: 15px;
  background: #fcfcfc;
}

.border-bottom { border-bottom: 1px solid #eee; }
.border-top { border-top: 1px solid #eee; }
.mr-10 { margin-right: 10px; }
.mt-10 { margin-top: 10px; }

.batch-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  padding: 8px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  animation: slideInDown 0.3s forwards;
  
  .selected-count {
    color: #1890ff;
    font-weight: 500;
  }
  
  .batch-actions {
    display: flex;
    gap: 10px;
  }
}

.table-wrapper {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow-x: auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.premium-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  
  th {
    background: #fafafa;
    padding: 12px 8px;
    font-weight: 600;
    color: #333;
    border-bottom: 2px solid #e8e8e8;
    text-align: left;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  td {
    padding: 10px 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #444;
    line-height: 1.5;
  }
}

.voucher-row-top {
  &:hover td { background: #fdfdfd; }
  &.audited td { opacity: 0.9; }
  
  td {
    background: #fff;
    &:first-child, &:nth-child(2), &:nth-child(3), &:nth-child(11), &:nth-child(12) {
      border-bottom: 1px solid #e8e8e8;
    }
  }
}

.voucher-row-inner {
  &:hover td { background: #fdfdfd; }
  td { background: #fff; }
  .last-row { border-bottom: 1px solid #e8e8e8; }
}

.spacer-row {
  height: 8px;
  td {
    background: #f8f9fa;
    padding: 0;
    border: none;
  }
}

// 骨架样式
.cell-date { color: #888; font-family: 'Monaco', monospace; }
.cell-voucher {
  .voucher-link {
    display: block;
    font-weight: bold;
    color: #1890ff;
    margin-bottom: 4px;
    &:hover { color: #40a9ff; text-decoration: underline; }
  }
  .voucher-tags { display: flex; gap: 4px; }
}

.cell-code { font-family: 'Consolas', monospace; color: #555; }
.cell-subject { font-weight: 500; }
.cell-summary { color: #666; font-style: italic; }

.cell-amount {
  font-family: 'PingFang SC', system-ui;
  font-weight: 600;
  &.text-借 { color: #cf1322; }
  &.text-贷 { color: #389e0d; }
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  line-height: 1;
  
  &-success { background: #f6ffed; border: 1px solid #b7eb8f; color: #52c41a; }
  &-warning { background: #fff7e6; border: 1px solid #ffd591; color: #fa8c16; }
}

.tag {
  display: inline-block;
  padding: 0 4px;
  font-size: 10px;
  border-radius: 2px;
  line-height: 1.4;
  &-blue { background: #e6f7ff; color: #1890ff; border: 1px solid #91d5ff; }
}

.member-info {
  display: flex;
  flex-direction: column;
  .name { font-weight: 500; }
  .time { font-size: 11px; color: #999; }
}

.no-data {
  padding: 80px 0;
  .no-data-content {
    color: #999;
    i { font-size: 48px; margin-bottom: 15px; display: block; }
    p { font-size: 16px; }
  }
}

@keyframes slideInDown {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
</style>
